import * as bootstrap from 'bootstrap';
import * as html from 'smg/html';
import { BindToastActions, NewToastLifetime, NewToastStack, SetCustomRenderer, ToastHandle, ToastOptions, ToastType } from 'smg/toast';

const MAX_VISIBLE = 3;

const ICONS: Record<ToastType, string> = {
    success: '<svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>',
    warning: '<svg viewBox="0 0 24 24"><path d="M12 9v4M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>',
    error: '<svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>',
    info: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>',
};

const DISMISS_ICON = '<svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>';

const DEFAULTS: Record<ToastType, { delay: number; ariaLive: string }> = {
    success: { delay: 5000, ariaLive: 'polite' },
    warning: { delay: 8000, ariaLive: 'assertive' },
    error: { delay: 10000, ariaLive: 'assertive' },
    info: { delay: 5000, ariaLive: 'polite' },
};

let container: HTMLElement;
const stack = NewToastStack(MAX_VISIBLE);

function getContainer() {
    if (!container) {
        container = html.FromString(
            '<div class="toast-container position-fixed bottom-0 end-0 p-3"></div>',
        ).firstElementChild as HTMLDivElement;
        document.body.appendChild(container);
    }
    return container;
}

function render(opts: ToastOptions): ToastHandle | undefined {
    const c = getContainer();
    const delay = opts.duration ?? DEFAULTS[opts.type].delay;
    const ariaLive = DEFAULTS[opts.type].ariaLive;
    const durationSec = delay / 1000;

    stack.makeRoom();

    const actions = opts.actions ?? [];
    let actionsHTML = '';
    if (actions.length) {
        const items = actions.map(action => {
            const label = html.Escape(action.label);
            if (action.href !== undefined) {
                return `<a class="toast-action">${label}</a>`;
            }
            return `<button type="button" class="toast-action">${label}</button>`;
        }).join('');
        actionsHTML = `<div class="d-flex flex-wrap gap-3">${items}</div>`;
    }

    const el = html.FromString(`
    <div class="toast" role="${ariaLive === 'assertive' ? 'alert' : 'status'}" aria-live="${ariaLive}" aria-atomic="true">
        <div class="toast-accent toast-accent-${opts.type}"></div>
        <div class="toast-inner">
            <div class="toast-content">
                <div class="toast-icon toast-icon-${opts.type}">${ICONS[opts.type]}</div>
                <div class="toast-text">
                    <div class="toast-msg">${opts.message}</div>
                    ${actionsHTML}
                </div>
                <button type="button" class="toast-dismiss" aria-label="Dismiss notification">${DISMISS_ICON}</button>
            </div>
            <div class="toast-progress">
                <div class="toast-progress-bar toast-progress-${opts.type}" style="--toast-duration: ${durationSec}s"></div>
            </div>
        </div>
    </div>`).firstElementChild as HTMLDivElement;

    // The toast's lifetime (countdown, holds, every way out) is smg/toast's;
    // Bootstrap only animates, and the progress bar follows the countdown.
    const toast = new bootstrap.Toast(el, {
        autohide: false,
    });
    const bar = el.querySelector('.toast-progress-bar') as HTMLElement | null;
    const life = NewToastLifetime({
        $toast: el,
        delay,
        close: () => toast.hide(),
        teardown: () => {
            toast.dispose();
            el.remove();
        },
        onCounting: counting => {
            if (bar) {
                bar.style.animationPlayState = counting ? 'running' : 'paused';
            }
        },
    });
    stack.add(life);
    el.addEventListener('hidden.bs.toast', life.end);
    el.querySelector('.toast-dismiss')?.addEventListener('click', life.handle.hide);

    BindToastActions(el.querySelectorAll('.toast-action'), actions, life.host);

    c.appendChild(el);
    toast.show();

    return life.handle;
}

export function InitToasts() {
    SetCustomRenderer(render);
}
